import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KeyRound, Search, ShieldCheck, Mail, Lock, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const emptyPolicy = { min_length: 6, require_uppercase: false, require_numbers: false, require_special: false, expiry_days: 0 };

export default function PasswordMaster() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [policy, setPolicy] = useState(emptyPolicy);
  const [savedPolicy, setSavedPolicy] = useState(emptyPolicy);
  const [resetFor, setResetFor] = useState(null);
  const [mode, setMode] = useState("email");
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/password-master/users").then(r => setUsers(r.data)).catch(e => toast.error(formatErr(e)));
    api.get("/password-policy").then(r => { setPolicy(r.data); setSavedPolicy(r.data); }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const filtered = users.filter(u => !q ||
    (u.name || "").toLowerCase().includes(q.toLowerCase()) ||
    (u.email || "").toLowerCase().includes(q.toLowerCase()));
  const pg = usePagination(filtered, q);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return "—"; }
  };

  const doReset = async () => {
    setBusy(true);
    try {
      const payload = mode === "direct" ? { mode, password: newPw } : { mode };
      const { data } = await api.post(`/password-master/users/${resetFor.id}/reset`, payload);
      if (mode === "email" && data.email_sent === false) toast.warning("Reset email could not be delivered");
      else toast.success(mode === "direct" ? "Password updated" : `Reset link sent to ${resetFor.email}`);
      setResetFor(null); setNewPw(""); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const savePolicy = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/password-policy", policy);
      setPolicy(data); setSavedPolicy(data);
      toast.success("Password policy saved");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const policyDirty = JSON.stringify(policy) !== JSON.stringify(savedPolicy);

  return (
    <div className="p-8" data-testid="password-master-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><KeyRound className="w-3 h-3"/> Owner only · Credentials control</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Password Master</h1>
        </div>
        <div className="relative w-72">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <Input data-testid="pm-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="pl-8 rounded-sm bg-secondary border-border font-mono text-sm h-10"/>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden mb-8">
        <table className="w-full text-sm" data-testid="pm-users-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">User Name</th><th className="text-left p-3">Email</th>
              <th className="text-left p-3">Role</th><th className="text-left p-3">Last Password Changed</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pg.pageItems.map(u => (
              <tr key={u.id} data-testid={`pm-user-row-${u.id}`} className="border-t border-border">
                <td className="p-3 font-semibold">{u.name}</td>
                <td className="p-3 font-mono text-xs">{u.email}</td>
                <td className="p-3"><span className="font-mono text-xs uppercase tracking-wider text-primary">{u.role}</span></td>
                <td className="p-3 text-xs text-muted-foreground">{fmtDate(u.password_changed_at)}</td>
                <td className="p-3 text-right">
                  <Button data-testid={`pm-reset-${u.id}`} size="sm" variant="outline" className="rounded-sm text-xs uppercase tracking-wider"
                    onClick={() => { setResetFor(u); setMode("email"); setNewPw(""); }}>
                    <RotateCcw className="w-3 h-3 mr-1"/> Reset Password
                  </Button>
                </td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground text-xs uppercase tracking-widest">No users match</td></tr>}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>

      <div className="border border-border bg-card rounded-sm p-6 max-w-2xl" data-testid="password-policy-card">
        <div className="text-primary text-xs uppercase tracking-[0.3em] mb-1 flex items-center gap-2"><ShieldCheck className="w-3 h-3"/> Policy</div>
        <h2 className="font-display font-black text-2xl uppercase tracking-tighter mb-5">Password Policy</h2>
        <div className="grid grid-cols-2 gap-x-8 gap-y-5">
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Minimum Length</Label>
            <Input data-testid="policy-min-length" type="number" min={4} value={policy.min_length}
              onChange={(e) => setPolicy(p => ({ ...p, min_length: parseInt(e.target.value) || 0 }))}
              className="mt-1 rounded-sm bg-secondary border-border font-mono"/>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Expiry Period (days · 0 = never)</Label>
            <Input data-testid="policy-expiry" type="number" min={0} value={policy.expiry_days}
              onChange={(e) => setPolicy(p => ({ ...p, expiry_days: parseInt(e.target.value) || 0 }))}
              className="mt-1 rounded-sm bg-secondary border-border font-mono"/>
          </div>
          {[
            ["require_uppercase", "Require Uppercase Letters", "policy-uppercase"],
            ["require_numbers", "Require Numbers", "policy-numbers"],
            ["require_special", "Require Special Characters", "policy-special"],
          ].map(([k, label, tid]) => (
            <div key={k} className="flex items-center justify-between border border-border rounded-sm px-3 py-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</Label>
              <Switch data-testid={tid} checked={policy[k]} onCheckedChange={(v) => setPolicy(p => ({ ...p, [k]: v }))}/>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-6">
          <Button data-testid="policy-save" onClick={savePolicy} disabled={busy || !policyDirty}
            className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">Save Policy</Button>
          <Button data-testid="policy-cancel" variant="outline" onClick={() => setPolicy(savedPolicy)} disabled={!policyDirty}
            className="rounded-sm uppercase tracking-[0.15em] text-xs">Cancel</Button>
        </div>
      </div>

      <Dialog open={!!resetFor} onOpenChange={(v) => { if (!v) setResetFor(null); }}>
        <DialogContent className="rounded-sm bg-card border-border max-w-md" data-testid="pm-reset-dialog">
          <DialogHeader><DialogTitle className="font-display uppercase">Reset Password · {resetFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="pm-mode-email" onClick={() => setMode("email")}
                className={`border rounded-sm p-3 text-left transition-colors ${mode === "email" ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}>
                <Mail className="w-4 h-4 text-primary mb-1"/>
                <div className="text-xs font-bold uppercase tracking-wider">Email Reset Link</div>
                <div className="text-[10px] text-muted-foreground mt-1">Send a reset link to {resetFor?.email}</div>
              </button>
              <button data-testid="pm-mode-direct" onClick={() => setMode("direct")}
                className={`border rounded-sm p-3 text-left transition-colors ${mode === "direct" ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}>
                <Lock className="w-4 h-4 text-primary mb-1"/>
                <div className="text-xs font-bold uppercase tracking-wider">Set Directly</div>
                <div className="text-[10px] text-muted-foreground mt-1">Type a new password for this user</div>
              </button>
            </div>
            {mode === "direct" && (
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">New Password</Label>
                <Input data-testid="pm-new-password" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                  className="mt-1 rounded-sm bg-secondary border-border font-mono"/>
              </div>
            )}
            <Button data-testid="pm-reset-confirm" onClick={doReset} disabled={busy || (mode === "direct" && !newPw)}
              className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
              {busy ? "Working…" : mode === "direct" ? "Set New Password" : "Send Reset Link"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
